import React, { useState } from 'react';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, Users, Building2, Key } from 'lucide-react';
import CompanyPermissionsManager from '../../components/rbac/CompanyPermissionsManager';
import CompanyRolesManager from '../../components/rbac/CompanyRolesManager';
import CompanyDepartmentsManager from '../../components/rbac/CompanyDepartmentsManager';
import { useI18n } from '../../contexts/I18nContext';

type Tab = 'permissions' | 'roles' | 'departments';

const tabConfig: Record<
  Tab,
  {
    label: string;
    description: string;
    icon: React.ComponentType<{ size?: number }>;
  }
> = {
  permissions: {
    label: 'Permissões',
    description: 'Gerencie permissões granulares por recurso e ação.',
    icon: Key,
  },
  roles: {
    label: 'Roles',
    description: 'Crie e gerencie roles customizados com permissões.',
    icon: Shield,
  },
  departments: {
    label: 'Departamentos',
    description: 'Organize usuários em departamentos hierárquicos.',
    icon: Building2,
  },
};

const RBACManagement: React.FC = () => {
  const { t } = useI18n();
  const { currentCompany } = useCompany();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('permissions');

  if (!currentCompany) {
    return (
      <div
        style={{
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        <p style={{ margin: 0 }}>{t('rbac.management.selectCompany')}</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div
        style={{
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        <p style={{ margin: 0 }}>{t('rbac.management.adminOnly')}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        <h2 style={{ margin: '0 0 6px 0' }}>{t('rbac.management.title')}</h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{t('rbac.management.subtitle')}</p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {(Object.keys(tabConfig) as Tab[]).map((tab) => {
          const Icon = tabConfig[tab].icon;
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `1px solid ${isActive ? '#2563eb' : '#e5e7eb'}`,
                backgroundColor: isActive ? '#eff6ff' : '#fff',
                color: isActive ? '#2563eb' : '#111827',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: isActive ? '600' : '500',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={18} />
              <span>{tabConfig[tab].label}</span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        {activeTab === 'permissions' && (
          <CompanyPermissionsManager companyId={currentCompany.id} />
        )}
        {activeTab === 'roles' && (
          <CompanyRolesManager companyId={currentCompany.id} />
        )}
        {activeTab === 'departments' && (
          <CompanyDepartmentsManager companyId={currentCompany.id} />
        )}
      </div>
    </div>
  );
};

export default RBACManagement;

